/* eslint-disable no-extra-parens */
import { IconDefinition, faXmark, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DropDownItem, DropDownItemId } from '@shared/types';
import { FormControl } from '@angular/forms';

@Component({
    selector: 'app-dropdown-menu',
    templateUrl: './dropdown-menu.component.html',
    styleUrls: ['./dropdown-menu.component.scss'],
    standalone: false,
})
export class DropdownMenuComponent implements OnChanges {
    @Input() isHidden = false;
    @Input() selectedIndex = -1;
    @Input() contentList: DropDownItem[] = [];

    @Output() readonly selectedIndexChange = new EventEmitter<number>();
    @Output() readonly selectedItemName = new EventEmitter<string>();
    @Output() readonly selectedItemId = new EventEmitter<number>();
    @Output() readonly isHiddenChange = new EventEmitter<boolean>();

    public closeIcon = faXmark;
    public faArrowLeft: IconDefinition = faArrowLeft;
    public displayList: DropDownItem[] = [];
    public searchTerm: string = '';
    public displayedIndex = -1;
    public searchControl = new FormControl<string>('');

    constructor() {}

    ngOnChanges(_: SimpleChanges): void {
        this.reset();
    }

    // Type guard to check if an item has an 'id'
    private hasId(item: DropDownItem): boolean {
        return (item as DropDownItemId).id !== undefined;
    }

    reset(): void {
        if (this.selectedIndex < 0) {
            this.selectedIndex = 0;
        }
        // Clone and sort contentList by name
        this.displayList = [...this.contentList].sort((d1, d2) => d1.name.localeCompare(d2.name));

        // Apply search filter if one is present
        if (this.searchTerm) {
            this.displayList = this.displayList.filter(item =>
                item.name.toLowerCase().includes(this.searchTerm.toLowerCase()),
            );
        }

        // Update displayedIndex based on the current selected item,
        // using id comparison when possible.
        const currentItem = this.contentList[this.selectedIndex];
        if (currentItem) {
            if (this.hasId(currentItem)) {
                const newIndex = this.displayList.findIndex(item =>
                    this.hasId(item) && (item as DropDownItemId).id === (currentItem as DropDownItemId).id,
                );
                this.displayedIndex = newIndex >= 0 ? newIndex : this.displayedIndex;
            } else {
                const newIndex = this.displayList.findIndex(item => item === currentItem);
                this.displayedIndex = newIndex >= 0 ? newIndex : this.displayedIndex;
            }
        } else {
            this.displayedIndex = -1;
        }
    }

    selectItem(value: DropDownItem, index: number): void {
        if (this.hasId(value)) {
            const idx = this.contentList.findIndex(item =>
                this.hasId(item) && (item as DropDownItemId).id === (value as DropDownItemId).id,
            );
            this.selectedIndex = idx >= 0 ? idx : this.selectedIndex;
        } else {
            this.selectedIndex = this.contentList.indexOf(value);
        }
        this.displayedIndex = index;
        this.selectedIndexChange.emit(this.selectedIndex);
        const item = this.contentList[this.selectedIndex];
        this.selectedItemName.emit(item.name);
        if (this.hasId(item)) {
            this.selectedItemId.emit((item as DropDownItemId).id);
        }
    }

    public hide(): void {
        this.isHidden = true;
        this.isHiddenChange.emit(true);
    }

    trackDropdownItem(_index: number, item: DropDownItem): string {
        return item.name;
    }

    filterDisplayList(): void {
        const term = this.searchControl.value?.toLowerCase() || '';

        // Filter the display list based on search term
        this.displayList = [...this.contentList]
            .sort((d1, d2) => d1.name.localeCompare(d2.name))
            .filter(item => item.name.toLowerCase().includes(term));

        // Bail out if selectedIndex is out of bounds
        if (this.selectedIndex < 0 || this.selectedIndex >= this.contentList.length) {
            this.displayedIndex = -1;
            return;
        }

        const currentItem = this.contentList[this.selectedIndex];
        if (currentItem) {
            if (this.hasId(currentItem)) {
                const newIndex = this.displayList.findIndex(item =>
                    this.hasId(item) && (item as DropDownItemId).id === (currentItem as DropDownItemId).id,
                );
                // Do not force fallback; instead, set to -1 if the current item is filtered out.
                this.displayedIndex = newIndex >= 0 ? newIndex : -1;
            } else {
                const newIndex = this.displayList.findIndex(item => item === currentItem);
                this.displayedIndex = newIndex >= 0 ? newIndex : -1;
            }
        } else {
            this.displayedIndex = -1;
        }
    }

    onDebouncedSearch(event: any): void {
        this.filterDisplayList();
    }
}
